// A camera's position-based identity: its 1-based ordinal (what the reader counts, as opposed to the
// array index) and the label shown wherever a reader meets one - legend, picker, tooltip, a Section's
// camera readout. Used by both Active groups' camera series charts and the shared production-group
// management modal, so it lives outside either page rather than one reaching into the other's module.

import { i18n } from '@/plugins/internationalization/i18n';
import type { CameraSeries } from '@/types/SectionLiveKpi';

/** A camera's 1-based ordinal - what the reader counts, as opposed to the array index. */
export function cameraOrdinal(index: number): number {
    return index + 1;
}

/**
 * A camera named by its position alone - for a Sensor with no name, and for any surface spanning two
 * Sections whose cameras are different devices.
 */
function cameraOrdinalLabel(index: number): string {
    return i18n.global.t('views.monitoring.activeGroups.texts.cameraOrdinal', { ordinal: cameraOrdinal(index) }) as string;
}

/**
 * What to call a camera anywhere a reader meets one - legend, picker, tooltip: its own name, or its ordinal
 * when the Sensor has none. Never a blank or an id.
 *
 * The name alone, not the ordinal and the name together: mounted Sensors are already named with their own
 * numbering ("Sektion 17 - kamera 2"), so pairing them reads as two disagreeing numbers for one device.
 */
export function cameraLabel(camera: Pick<CameraSeries<unknown>, 'name'>, index: number): string {
    return camera.name?.trim() || cameraOrdinalLabel(index);
}
